import { Injectable } from "@angular/core";
import { AlertController } from "@ionic/angular";
import { OidcSecurityService } from "angular-auth-oidc-client";
import { DetailAktDataRetrieveResponse } from "../../api/models/detail-akt-data-retrieve-response";
import { SendService } from "../../api/services/send.service";

@Injectable({
  providedIn: "root",
})
export class EmailService {
  constructor(
    private sendService: SendService,
    private oidcSecurityService: OidcSecurityService,
    private alertController: AlertController,
  ) {}

  private sendMail(aktid) {
    this.sendService
      .sendStatusCreate({
        akt_id: aktid,
        token: this.oidcSecurityService.getToken(),
        submit_type: "email",
      })
      .subscribe(() => {
        alert("E-Mail gesendet");
      });
  }

  private sendVermittlerMail(aktid) {
    this.sendService
      .sendStatusCreate({
        akt_id: aktid,
        token: this.oidcSecurityService.getToken(),
        submit_type: "vermittleremail",
      })
      .subscribe(() => {
        alert("E-Mail gesendet");
      });
  }

  async sendEmailAlert(aktId) {
    const alert = await this.alertController.create({
      header: "Wollen Sie eine Statusinfo E-Mail verschicken?",
      message: "Bei Bestätigung wird eine E-Mail an den Versicherungsnehmer gesendet.",
      buttons: [
        {
          text: "Abbrechen",
          role: "cancel",
          cssClass: "color-primary",
        },
        {
          cssClass: "color-secondary",
          text: "Senden",
          handler: () => {
            this.sendMail(aktId);
          },
        },
      ],
    });

    await alert.present();
  }

  async sendVermittlerEmailAlert(aktId) {
    const alert = await this.alertController.create({
      header: "Wollen Sie eine Statusinfo E-Mail an den Vermittler verschicken?",
      message: "Bei Bestätigung wird eine E-Mail an den Vermittler gesendet.",
      buttons: [
        {
          text: "Abbrechen",
          role: "cancel",
          cssClass: "color-primary",
        },
        {
          cssClass: "color-secondary",
          text: "Senden",
          handler: () => {
            this.sendVermittlerMail(aktId);
          },
        },
      ],
    });

    await alert.present();
  }

  getEmailValidationRegex(): RegExp {
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]+$/;
  }

  getDefaultEmailSubject(
    details: DetailAktDataRetrieveResponse,
    relevantDate: Date = new Date(),
    originalDefaultSubject?: string,
    delimiter: string = " ",
  ): string {
    let betreffPrefix: string = "";
    if (details?.Contacts?.VU?.Name.includes("Helvetia")) {
      betreffPrefix = `${new Date(relevantDate).toLocaleDateString("de", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })} ${relevantDate.toLocaleTimeString("de", { hour: "2-digit", minute: "2-digit" })} - `;
    }
    if (details?.Contacts?.VU?.Name.includes("ERGO")) {
      betreffPrefix = `Termininfo SNR ${details.Incident_number} - `;
    }

    const defaultGeneratedSubjectWithoutPrefix = `Betreffend Auftrag [${details.Title}] Schadennummer [${details.Incident_number}]`;
    let defaultGeneratedSubject: string = `${betreffPrefix}${defaultGeneratedSubjectWithoutPrefix}`;

    if (details.Contacts?.AST?.Name || details.Contacts?.VN?.Name) {
      defaultGeneratedSubject += ` Anspruchsteller [${details.Contacts?.AST?.Name ?? details.Contacts?.VN?.Name}]`;
    }

    if (
      originalDefaultSubject &&
      this.subjectAlreadyContainsDefault(defaultGeneratedSubjectWithoutPrefix, originalDefaultSubject)
    ) {
      return originalDefaultSubject;
    }
    return `${defaultGeneratedSubject}${originalDefaultSubject ? delimiter + originalDefaultSubject : ""}`;
  }

  normalizeSubject(str: string): string {
    if (!str) return "";

    return str
      .replace(/\[.*?\]/g, "")
      .replace(/schadennummer/gi, "schadennr")
      .replace(/schadennr\./gi, "schadennr")
      .replace(/\s+/g, "")
      .toLowerCase();
  }

  subjectAlreadyContainsDefault(defaultSubject: string, existingSubject: string): boolean {
    const normDefault = this.normalizeSubject(defaultSubject);
    const normExisting = this.normalizeSubject(existingSubject);

    return normExisting.includes(normDefault);
  }
}
